/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/core'
import { useState, useEffect, useRef, useCallback } from 'react';
import { message, Button, Input, Checkbox, Icon } from 'antd';
import DateRangePicker from '../../_shared/date-range-picker';
import styles from './reconciliation.styles';
import moment from 'moment';
import LNSearch from '../../_shared/ln-search';
import { debounce, get, set } from 'lodash';
import { createReconciliation, deleteReconciliation, getReconciliation, updateOrder } from '../../_shared/services/orders.service';
import { TableList } from '../../_shared/table-list';
import { getConfig, getLicAddress, getRawLicense, getReconUrl, GPSID, loadLicense, searchTable, size, sortableDate, stopProp, toMoney, withSorters } from '../../actions/utils';
import { useSelector } from 'react-redux';
import { AdminOrderButton, OrderModal } from '../orders/orders.component';
import { createInvoice, getQuickbooksAuthUrl } from '../../_shared/services/quickbooks.service';
import { CashReturnIcon, CashPickupIcon } from '../../_shared/CustomIcons';
import Link from '../../_shared/link';
import Row from '../../_shared/row';
import { AddSelectBilling } from '../lnupdate';
import { updateStoredLicenseNumber } from '../../_shared/services/api.service';
import { getStatusIcon, OrderStatus } from '../../models/order';
import { Days } from '../drive-schedule/schedule';

export const EditRow = (t, row, key, props) => {
  const hasRecon = get(row, 'data.recon');
  if (hasRecon && !props.forceRender) { return t }

  return (
    <Input disabled={props.disabled} onClick={stopProp} value={t} title={t} loading={props.saving} onChange={e => props.onChangeText(e.target.value, row,  key)} />
  )
}

export const Reconciliation = props => {
  const user = useSelector(state => get(state, 'user.email'))
  const [state, setState] = useState({
    loading: false,
    saving: false,
    selectedDate: [moment().subtract(1, 'week'), moment()],
    // selectedDate: [moment().subtract(4, 'week'), moment().subtract(1, 'week')],
    vendor: undefined,// { 'License #': '404R-00176' },
    data: [],
    selectedRows: [],
    search: '',
    accountsOnly: true,
    editModal: undefined,
  })
  const [billingDirty, setBillingDirty] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const LNRef = useRef();
  const orderBtnRef = useRef();
  // Set briefly while we imperatively revert the LNSearch selection after a
  // user cancels the "unsaved changes" prompt, so handleVendorSelect ignores
  // the callback fired by that revert.
  const revertingVendorRef = useRef(false);
  // Mirror of billingDirty readable from async closures (e.g. retrieveData)
  // without stale-closure problems from useState.
  const billingDirtyRef = useRef(false);
  const markBillingDirty = (v) => { billingDirtyRef.current = v; setBillingDirty(v); };
  // Card #285 (Danielle 2026-09-03) — the reconciliation created by the last
  // "Export Rec" click, held only while its Reconciliation Return order is
  // still being filled in. See onRec / onAdminOrderClose below.
  const pendingReconRef = useRef(null);

  const loadData = () => {
    if (size(state.selectedDate) && state.vendor) {
      retrieveData();
    } else {
      setState(s => ({ ...s, data: [] }));
    }
  }

  useEffect(() => {
    loadData();
  }, [state.selectedDate, state.vendor]);

  const retrieveData = async () => {
    try {
      setState(s => ({ ...s, loading: true }));
      const data = (await getReconciliation({ 
        selectedDate: state.selectedDate,
        vendor: state.vendor,
      })).sort((_a, _b) => {
        const a = !!get(_a, 'data.recon');
        const b = !!get(_b, 'data.recon');
        const adate = moment(get(_a, 'data.order.deliveryDate')).startOf('day');
        const bdate = moment(get(_b, 'data.order.deliveryDate')).startOf('day');

        const aisPickup = get(_a, 'isPickup');
        const bisPickup = get(_a, 'isPickup');
        const ad = get(_a, `data.${aisPickup ? 'pickupCompany' : 'destCompany'}.DBA`)
        const bd = get(_b, `data.${bisPickup ? 'pickupCompany' : 'destCompany'}.DBA`)
        
        if (a == b) {
          if (adate == bdate) {
            return ad.localeCompare(bd, 'en', { numeric: true })
          }
          return adate - bdate;
        }
        return a - b;
      })

      setState(s => {
        const next = { ...s, data, selectedRows: [] };
        // Don't clobber in-flight billing edits on date-range reloads.
        if (!billingDirtyRef.current) {
          next.vendorProps = getRawLicense(get(s, 'vendor.License #'));
        }
        return next;
      })
    } catch (err) {
      message.error(err.message);
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  const selectDate = date => {
    setState(s => ({ ...s, selectedDate: date }))
  }

  // Read the value out of the event BEFORE the updater. A functional setState runs after
  // the handler returns, by which point React 16 has recycled the synthetic event and
  // nulled its target, so `e.target.value` inside the updater throws and unmounts the
  // page on the second keystroke.
  const changeSearch = e => {
    const { value } = e.target;
    setState(s => ({ ...s, search: value }))
  }

  const saveOrder = useCallback(debounce(async (order) => {
    try {
      setState(s => ({ ...s, saving: true }));
      await updateOrder(order);
    } catch (err) {
      message.error(`${err.message}`);
    } finally {
      setState(s => ({ ...s, saving: false }));
    }
  }, 1500, { leading: false }), []);

  const onChangeText = (val, row, key) => {
    const orderIndex = state.data.findIndex(o => o.id === row.id);
    set(state, `data.${orderIndex}.${key}`, val);
    setState(s => ({ ...s, data: [...state.data] }))
    saveOrder(get(state, `data.${orderIndex}`));
  };

  const selectOrder = (order) => {
    const orderType = get(order, 'data.order.orderType');
    const cashReturn = get(order, 'data.order.cashRoute') || orderType === 'cash_return' || orderType === 'cash_pickup';
    setState(s => ({
      ...s,
      editModal: {
        isModal: true,
        state: {
          cashReturn,
          editOrder: order,
        }
      }
    }));
  };

  const onCloseEditModal = (shouldUpdate) => {
    setState(s => ({ ...s, editModal: undefined }));
    if (shouldUpdate) {
      loadData();
    }
  };

  const handleVendorSelect = v => {
    if (revertingVendorRef.current) {
      revertingVendorRef.current = false;
      return;
    }
    if (billingDirty) {
      const ok = window.confirm('You have unsaved billing changes that will be lost. Discard them and continue?');
      if (!ok) {
        const currentLN = get(state, 'vendor.License #');
        if (LNRef.current && LNRef.current.selectOption) {
          revertingVendorRef.current = true;
          LNRef.current.selectOption(currentLN || null);
        }
        return;
      }
    }
    const vendor = getRawLicense(v);
    markBillingDirty(false);
    setState(s => ({ ...s, selectedRows: [], vendor, vendorProps: vendor }))
  }

  // Card #277 round 3 — the grid hands back the rows it was showing, in their new order.
  // Under a search that is only the matches, and assigning it straight to `data` dropped
  // every row the search had hidden until the next reload. The moved rows go back into the
  // slots the shown rows held instead, and every hidden row stays where it was. (TableList
  // turns dragging off while a column sort is active: the grid's order is then the sort's,
  // not `data`'s, so a drop there could only move the wrong rows.)
  const reorderRows = rows => {
    setState(s => {
      const shown = new Set(rows.map(r => r.id));
      let next = 0;
      return {
        ...s,
        data: s.data.map(r => (shown.has(r.id) && next < rows.length ? rows[next++] : r)),
      };
    });
  }

  const onSelectedRowsChange = (keys) => {
    setState(s => ({ ...s, selectedRows: keys }));
  }

  const onPrint = async () => {
    var cssPagedMedia = (function () {
      var style = document.createElement('style');
      document.head.appendChild(style);
      return function (rule) {
        style.innerHTML = rule;
      };
    }());
  
    cssPagedMedia.size = function (size) {
      cssPagedMedia('@page {size: ' + size + '}');
    };
    
    cssPagedMedia.size('portrait');
    window.print();
    cssPagedMedia.size('landscape');

    await Promise.all(printRows.map(async order => {
      if (order.id) {
        set(order, 'data.recon', { date: moment().toISOString(), user })
        await updateOrder(order);
      }
    }))
    retrieveData();
  }

  const onRec = async () => {
    try {
      setState(s => ({ ...s, loading: true }))

      const rows = [...printRows];
      rows.pop();

      const hasRec = rows.filter(r => {
        return !!get(r, 'data.recon.file');
      })

      let cont = true;
      if (size(hasRec)) {
        cont = window.confirm(
`
These orders are already attached to Reconciliation forms.\n
${hasRec.map(r => r.orderId).join('\n')}\n
Do you wish to continue?
`
)
      }

      if (!cont) { return }

      const resp = await createReconciliation({
        vendor: state.vendor,
        rows,
        user,
        opts: {}
      })

      retrieveData();

      orderBtnRef.current.open({
        data: {
          order: {
            pickupLN: GPSID,
            destinationLN: get(state, 'vendor.License #'),
            preferredDelivery: moment().toISOString(),
            preferredPickup: moment().toISOString(),
            orderType: 'cash_return',
            notes: getReconUrl(resp.file),
            status: OrderStatus.orderApproved,
            cashReturn: true,
          },
          billing: {
            invoice: resp.reconId,
          },
          recon: {
            reconId: resp.reconId,
            file: resp.file,
            date: resp.date,
          }
        }
      })

      // Card #285 — /createreconciliation has already inserted the recon row,
      // but nothing links it to a Reconciliation Return order until the modal
      // just opened is SUBMITTED (POST /orders/ writes data.returnOrderId from
      // data.recon.reconId). Remember it so onAdminOrderClose can discard it if
      // the user backs out instead. Set only once the modal is actually up, so
      // a failed open can never leave a stale rollback target behind.
      pendingReconRef.current = resp.reconId;
    } catch (err) {
      message.error(err.toString());
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  // Card #285 (Danielle 2026-09-03) — "it should not create the recon unless you
  // hit submit on the Order creation Page (if the x button is selected then it
  // should not create)". Export Rec creates the recon row up front because the
  // Create Order modal has to be seeded with the new reconId/PDF, so instead of
  // deferring the insert we undo it when the order is never created. Without
  // this, the Reconciliations tab keeps a permanently orphaned row whose Order
  // ID / Return Date / Pickup / Destination / DBA are all blank, because every
  // one of those columns is derived from the return order.
  const onAdminOrderClose = async (shouldUpdate) => {
    const reconId = pendingReconRef.current;
    pendingReconRef.current = null;

    if (reconId && !shouldUpdate) {
      try {
        // Also unsets data.recon from the orders that were just exported, so
        // they are no longer marked as reconciled and can be exported again.
        await deleteReconciliation([reconId]);
        message.info('Reconciliation discarded — no Reconciliation Return order was created.');
      } catch (err) {
        const msg = get(err, 'response.data.error', err.message);
        message.warning(`Could not discard reconciliation ${reconId}: ${msg}. Delete it from the Reconciliations tab.`);
      }
    }

    loadData();
  };

  const onQBExport = async () => {
    let cont = true;
    try {
      if (billingDirty) {
        const proceed = window.confirm('You have unsaved billing changes. Save them before exporting to Quickbooks, or cancel to stay on this page.');
        if (!proceed) { return; }
        await saveBilling();
        if (billingDirtyRef.current) { return; }
      }
      setState(s => ({ ...s, loading: true }));
      const rows = [...printRows];
      rows.pop();

      const notekey = 'data.order.billingNotes';
      const hasNotes = rows.filter(r => get(r, notekey));
      const finalStatus = [OrderStatus.delivered, OrderStatus.exception, OrderStatus.fullrejection, OrderStatus.partialrejection];

      const isNotFinal = rows.filter(r => !finalStatus.includes(get(r, 'data.order.status')));
      const hasInvoice = rows.filter(r => get(r, 'data.billing.gpsinvoice'));

if (size(hasNotes)) {
cont = window.confirm(`
Billing notes found on these orders:

${hasNotes.map(r => `${r.orderId}: ${get(r, notekey)}`).join('\n')}

Do you wish to continue?  
`)
      }

if (size(isNotFinal)) {
cont = window.confirm(`
These orders are not Delivered or Rejected:

${isNotFinal.map(r => `${r.orderId}`).join('\n')}

Do you wish to continue?  
`)
      }

if (size(hasInvoice)) {
cont = window.confirm(`
These orders already have an invoice number attached to them:

${hasInvoice.map(r => `${r.orderId}`).join('\n')}

Do you wish to continue?  
`)
      }

      if (!cont) { return }

      const result = await createInvoice({
        vendor: {
          ...(state.vendor || {}),
          billing: get(state, 'vendor.billing', []).map(b => {
            return {
              ...b,
              value: Array.isArray(b.value) ? b.value.map(bv => Days[bv]) : b.value
            }
          })
        },
        rows,
      })
  
      const invoiceNum = get(result, 'DocNumber');
  
      if (invoiceNum) {
        await Promise.all(rows.map(async pr => {
          set(pr, 'data.billing.gpsinvoice', invoiceNum)
          await updateOrder(pr)
        }))
        message.success(`Invoice ${invoiceNum} created successfully!`);
      }
    } catch (err) {
      const msg = get(err, 'response.data.error', err.toString());
      message.error(msg)
    } finally {
      setState(s => ({ ...s, loading: false }));

      if (cont) {
        loadData();
      }
    }
  }

  const onLoaded = st => {
    // const vendor = st.allLns.find(ln => get(ln, 'License #') == 'Billing Test 420-R');
    // setState(s => ({ ...s, selectedRows: [], vendor }))
  }

  const goToRecon = row => {
    const file = get(row, 'data.recon.file');

    window.open(getReconUrl(file), '_blank');
  }

  // Keep latest vendorProps accessible in saveBilling's async path without re-creating the callback.
  const vendorPropsRef = useRef(state.vendorProps);
  useEffect(() => { vendorPropsRef.current = state.vendorProps; }, [state.vendorProps]);

  // Track edits locally; the user commits via the Save Changes button.
  const onVendorUpdate = useCallback((items) => {
    setState(s => ({ ...s, vendorProps: { ...s.vendorProps, billing: items } }));
    markBillingDirty(true);
  }, [])

  const saveBilling = async () => {
    try {
      setBillingSaving(true);
      // Send only the billing list — the one thing this screen owns. vendorProps is a
      // MERGED licence row (getRawLicense: the scraped blob with the lnstored override
      // spread over it) captured when the vendor was picked, so posting it whole wrote
      // that whole stale snapshot into the user-owned store: it baked the scrape's
      // Licensee/DBA/Street Address/City/Zip in permanently (lnstored shadows the blob,
      // so no later scrape could correct them) and re-authored the card-286 License
      // Override lock, re-locking a licence an admin had unlocked in the meantime.
      await updateStoredLicenseNumber({
        'License #': get(vendorPropsRef.current, 'License #'),
        billing: get(vendorPropsRef.current, 'billing', []),
      });
      await loadLicense();
      markBillingDirty(false);
      setState(s => ({ ...s, vendor: s.vendorProps }));
      message.success('Billing items saved.');
    } catch (err) {
      message.error(err.toString());
    } finally {
      setBillingSaving(false);
    }
  }

  let total = 0;
  const printRows = state.selectedRows.sort().map(key => {
    const row = get(state, `data`, []).find(d => d.id == key);
    const amt = parseFloat((get(row, 'data.billing.amount', 0) || 0).toString().replace('$', ''));
    total += amt;
    return row;
  });

  if (size(printRows)) {
    const toAdd = {};
    set(toAdd, 'data.billing.amount', toMoney(total));
    printRows.push(toAdd);
  }

  const canShowPrint = !!size(printRows);
  const showSpecial = getConfig('billing.showSpecial', false);

  const hasBillingRows = get(state, 'vendorProps.billing');

  // Card #277 — one array for the grid and the search box. Several of these cells show
  // something other than their dataIndex (Vendor and Destination are composed from the
  // licence record, City swaps to the pickup company on a pickup row, and the billing
  // cells are editable inputs), so the search reads the rendered cell as well as the
  // value behind it. Round 3 — and every column sorts; see the sortValue hooks below.
  const cols = withSorters(getCols({ onChangeText, goToRecon, selectOrder, ...state }));

  return (
    <div css={css(styles.container)}>
      <div className="no-print" css={css(styles.calContainer)}>
        <div css={css(styles.input, styles.row)}>
          <LNSearch ref={LNRef} onSelect={handleVendorSelect} accountsOnly={state.accountsOnly} onLoaded={onLoaded} />
          <Checkbox checked={state.accountsOnly} style={{ width: 130, marginLeft: 10, }} onChange={() => setState(s => ({ ...s, accountsOnly: !state.accountsOnly }))}>{'Registered Accounts Only'}</Checkbox>
        </div>
        
        <DateRangePicker
          css={css(styles.input)}
          loading={state.loading}
          value={state.selectedDate}
          onChange={selectDate}
        />

        <Input.Search
          placeholder="Search all columns..."
          value={state.search}
          allowClear
          style={{ width: 260, marginLeft: 10 }}
          onChange={changeSearch}
        />

        <AdminOrderButton onClose={onAdminOrderClose} ref={orderBtnRef} />

        {showSpecial &&
          <Button
            onClick={() => window.open(getQuickbooksAuthUrl(), '_blank')}
            style={{ marginLeft: 5 }}
            className="no-print"
            icon="link"
            title="Authorize this app to access QuickBooks"
          >
            Connect QuickBooks
          </Button>}

        {canShowPrint && showSpecial &&
          <Button
            onClick={onQBExport}
            style={{ marginLeft: 5 }}
            className="no-print"
            type="primary"
            icon="book"
          >
            Export to Quickbooks
          </Button>}

        {canShowPrint && showSpecial &&
          <Button 
            onClick={onRec} 
            css={css(styles.printBtn)} 
            className="no-print"
            type="primary" 
            icon="printer"
            loading={state.loading}
          >
            Export Rec
          </Button>}

        {canShowPrint && !showSpecial &&
          <Button 
            onClick={onPrint} 
            css={css(styles.printBtn)} 
            className="no-print"
            type="primary" 
            icon="printer"
          >
            Print / Export
          </Button>}
      </div>

      {!!state.vendor && <Row className='no-print'>
        <AddSelectBilling
          loading={state.vendorPropsLoading || billingSaving}
          value={hasBillingRows}
          onChange={onVendorUpdate}
          renderFooter={() => (
            <Button
              type="primary"
              icon="save"
              onClick={saveBilling}
              disabled={!billingDirty || billingSaving}
              loading={billingSaving}
              style={{ marginLeft: 8 }}
            >
              {billingDirty ? 'Save Changes' : 'Saved'}
            </Button>
          )}
        />
      </Row>}

      <div className="print-only">
        <div css={css(styles.headerRow)}>{`Vendor Reconciliation Form`}</div>
        <div css={css(styles.headerSub)}>{`Vendor Name: ${get(state, 'vendor.DBA') || get(state, 'vendor.Licensee', 'none')} Date: ${moment().format('L')}`}</div>
        
        <TableList
          data={printRows}
          columns={printCols()}
          canSelectRows={false}
          showPagination={false}
          implementScroll={false}
        />

        <div css={css(styles.printLowerText)}>
          <br /><br />
          <div><b>{`Courier: `}</b>{`By signing this agreement, I understand that I am responsible for the aforementioned payments and paperwork, and agree to deliver them to the appropriate vendor.`}</div>
          <br />
          <div css={css(styles.row)}>
            <div css={css(styles.sig)}>{`Courier Signature:______________________________`}</div>
            <div css={css(styles.sig)}>{`Manager Signature:______________________________`}</div>
          </div>
          
          <br /><br />
          <div><b>{`Vendor: `}</b>{`By signing this agreement, I agree that the aforementioned payments and paperwork have been delivered to the correct vendor facility, and I have verified that the information provided is correct and accurate. All payment discrepancies need to be reported to management within 48 hours. Transporter is not liable for any payment discrepancies reported after 48 hours.`}</div>
          <br />
          <div css={css(styles.row)}>
            <div css={css(styles.sig)}>{`Vendor Rep (Print):______________________________`}</div>
            <div css={css(styles.sig)}>{`Vendor Rep (Sign):______________________________`}</div>
          </div>
        </div>
      </div>

      <div className="no-print">
        <TableList
          draggable
          loading={state.loading}
          pageSize={500}
          data={searchTable(cols, state.data, state.search, { useRender: true })}
          onReorder={reorderRows}
          columns={cols}
          onSelectedRowsChange={onSelectedRowsChange}
          onRefresh={loadData}
          selectedRowKeys={state.selectedRows}
        />
      </div>
      <OrderModal editModal={state.editModal} onClose={onCloseEditModal} />
    </div>
  )
}

// Card #277 round 3 — the text the Vendor, Destination and City cells paint. None of them is
// its column's dataIndex (the name is DBA-else-Licensee, and a pickup row switches
// Destination and City over to GPS and the pickup company), so the cell and its sorter both
// build the text here, and a header click orders the rows by exactly what the cells say.
const vendorText = row => {
  const name = 'pickupCompany';
  const compName = get(row, `data.${name}.DBA`, get(row, `data.${name}.Licensee`));
  const addr = getLicAddress(get(row, 'data.order.pickupLN'));
  return `${compName || ''}${compName ? ' - ' : ''}${addr}`;
}

const destinationText = row => {
  const isPickup = get(row, 'isPickup');
  const name = isPickup ? 'pickupCompany' : 'destCompany';
  const compName = get(row, `data.${name}.DBA`, get(row, `data.${name}.Licensee`));
  const compAddress = get(row, `data.${name}.Street Address`);
  const zip = get(row, `data.${name}.Zip`);
  return isPickup ? 'GPS' : `${compName || ''}${compName ? ' - ' : ''}${compAddress}${zip ? ', ' + zip : ''}`;
}

const cityText = row => (get(row, 'isPickup') ? get(row, 'data.pickupCompany.City') : get(row, 'data.destCompany.City'));

// Amount is typed in by hand ("120", "$120.50", "1,200"), so it sorts as money. Anything
// that still isn't a number sorts as text, after the amounts.
const amountSortValue = row => {
  const val = get(row, 'data.billing.amount');
  const num = parseFloat(`${val === null || val === undefined ? '' : val}`.replace(/[$,\s]/g, ''));
  return isNaN(num) ? val : num;
}

const getCols = props => {
  return [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      width: '8%',
      render: (text, row) => {
        const hasRecon = get(row, 'data.recon.file');
        const ico = getStatusIcon(row);
        const isCashReturn = get(row, 'data.order.cashRoute') || get(row, 'data.order.orderType') === 'cash_return';
        const isCashPickup = get(row, 'data.order.orderType') === 'cash_pickup';

        return (
          <div>
            <div style={{ fontWeight: hasRecon ? 'bold' : 'normal' }}>
              <Link onClick={(e) => { e.stopPropagation(); props.selectOrder(row); }}>{text}</Link>
            </div>
            <Icon
              type={ico.icon}
              title={ico.title}
              css={css(`color: ${ico.color};`)}
            />
            {isCashReturn && <CashReturnIcon />}
            {isCashPickup && <CashPickupIcon />}
            {hasRecon && <Icon type="form" title="Recon Form" onClick={() => props.goToRecon(row)} />}
          </div>
        )
      }
    },
    {
      title: 'Delivery Date',
      dataIndex: 'data.order.deliveryDate',
      width: '7%',
      sortValue: row => sortableDate(get(row, 'data.order.deliveryDate')),
      render: (t) => {
        return moment(t).format('L');
      }
    },
    {
      title: 'Notes',
      dataIndex: 'data.order.notes',
      width: '4%',
      render: (t, row) => {
        return <div css={css(styles.ellipsis)} title={t}>{t}</div>
      }
    },
    {
      title: 'Billing Notes',
      dataIndex: 'data.order.billingNotes',
      width: '7%',
      render: (t, row) => {
        return EditRow(t, row, 'data.order.billingNotes', props);
      }
    },
    {
      title: 'Box Count',
      dataIndex: 'data.order.weight',
      width: '8%',
      render: (t, row) => {
        return EditRow(t, row, 'data.order.weight', props);
      }
    },
    {
      title: 'Vendor',
      dataIndex: 'data.pickupCompany.Licensee',
      width: '13%',
      sortValue: vendorText,
      render: (t, row) => {
        return <span>{vendorText(row)}</span>
      }
    },
    {
      title: 'Destination',
      dataIndex: 'data.destCompany.Street Address',
      width: '13%',
      sortValue: destinationText,
      render: (t, row) => {
        return <span>{destinationText(row)}</span>
      }
    },
    {
      title: 'City',
      dataIndex: 'data.destCompany.City',
      width: '5%',
      sortValue: cityText,
      render: (t, row) => {
        return <span>{cityText(row)}</span>
      }
    },
    {
      title: 'Invoice',
      dataIndex: 'data.billing.invoice',
      width: '7%',
      render: (t, row) => {
        return EditRow(t, row, 'data.billing.invoice', props);
      }
    },
    {
      title: 'Amount',
      dataIndex: 'data.billing.amount',
      width: '7%',
      sortValue: amountSortValue,
      render: (t, row) => {
        return EditRow(t, row, 'data.billing.amount', props);
      }
    },
    {
      title: 'Method',
      dataIndex: 'data.billing.method',
      width: '7%',
      render: (t, row) => {
        return EditRow(t, row, 'data.billing.method', props);
      }
    },
    {
      title: 'Manifest',
      dataIndex: 'data.billing.manifest',
      width: '7%',
      render: (t, row) => {
        return EditRow(t, row, 'data.billing.manifest', props);
      }
    },
    {
      title: 'Delivery Invoice',
      dataIndex: 'data.billing.gpsinvoice',
      width: '7%',
      render: (t, row) => {
        return EditRow(t, row, 'data.billing.gpsinvoice', { ...props, forceRender: true });
      }
    },
  ]
}

const printCols = props => {
  return [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      width: '15%',
    },
    {
      title: 'Destination',
      dataIndex: 'data.destCompany.Street Address',
      width: '20%',
      render: (t, row) => {
        const isPickup = get(row, 'isPickup');
        const name = isPickup ? 'pickupCompany' : 'destCompany';
        const compName = get(row, `data.${name}.DBA`, get(row, `data.${name}.Licensee`));
        const compAddress = get(row, `data.${name}.Street Address`);
        const zip = get(row, `data.${name}.Zip`);
        const text = isPickup ? 'GPS' : `${compName || ''}${compName ? ' - ' : ''}${compAddress}${zip ? ', ' + zip : ''}`;
        if (!zip) { return null }
        return text;
      }
    },
    {
      title: 'City',
      dataIndex: 'data.destCompany.City',
      width: '10%',
      render: (t, row) => {
        const isPickup = get(row, 'isPickup');
        const text = isPickup ? get(row, 'data.pickupCompany.City') : get(row, 'data.destCompany.City');
        return text
      }
    },
    {
      title: 'Date',
      dataIndex: 'deliveryDate',
      width: '10%',
      render: (t, row) => {
        if (!t) { return '' }
        return moment(t).format('L');
      }
    },
    {
      title: 'Invoice',
      dataIndex: 'data.billing.invoice',
      width: '15%',
      render: (t) => {
        return t || '';
      }
    },
    {
      title: 'Amount',
      dataIndex: 'data.billing.amount',
      width: '15%',
      render: (t) => {
        return t || '';
      }
    },
    {
      title: 'Method',
      dataIndex: 'data.billing.method',
      width: '15%',
      render: (t) => {
        return t || '';
      }
    },
  ]
}